// src/components/RichText.tsx
import React, { useEffect, useRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import katex from 'katex';
import renderMathInElement from 'katex/contrib/auto-render';
import { useTheme } from '@mui/material/styles';
import 'katex/dist/katex.min.css';

interface Props {
  html: string;
  className?: string;
}

/**
 * TipTap mathematics often serializes inline equations as:
 *   <span data-type="inline-math" data-latex="..."></span>
 * KaTeX auto-render does not understand this shape, so we convert these nodes
 * into standard delimiter text (\(...\) / \[...\]) before rendering.
 */
function normaliseTiptapMathNodes(root: HTMLElement) {
  const nodes = root.querySelectorAll<HTMLElement>('[data-latex][data-type]');
  nodes.forEach((node) => {
    const latex = node.getAttribute('data-latex');
    if (!latex) return;

    const type = (node.getAttribute('data-type') || '').toLowerCase();
    const isBlock =
      type.includes('block') ||
      type.includes('display') ||
      node.tagName.toLowerCase() === 'div';

    const delimited = isBlock ? `\\[${latex}\\]` : `\\(${latex}\\)`;

    // Replace the node with plain text so KaTeX auto-render can pick it up.
    node.replaceWith(document.createTextNode(delimited));
  });
}

function renderDelimitedMathTextNodes(root: HTMLElement) {
  const tokenRegex = /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$\n]+?\$)/g;

  const renderToken = (token: string): HTMLElement | null => {
    const isBlock = token.startsWith('$$') || token.startsWith('\\[');
    let latex = token;

    if (token.startsWith('$$') && token.endsWith('$$')) {
      latex = token.slice(2, -2);
    } else if (token.startsWith('$') && token.endsWith('$')) {
      latex = token.slice(1, -1);
    } else if (token.startsWith('\\(') && token.endsWith('\\)')) {
      latex = token.slice(2, -2);
    } else if (token.startsWith('\\[') && token.endsWith('\\]')) {
      latex = token.slice(2, -2);
    } else {
      return null;
    }

    const span = document.createElement(isBlock ? 'div' : 'span');
    span.innerHTML = katex.renderToString(latex, {
      displayMode: isBlock,
      throwOnError: false,
    });
    return span;
  };

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    const parent = current.parentElement;
    if (
      parent &&
      !parent.closest('.katex') &&
      parent.tagName !== 'SCRIPT' &&
      parent.tagName !== 'STYLE'
    ) {
      textNodes.push(current as Text);
    }
    current = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const text = textNode.textContent ?? '';
    tokenRegex.lastIndex = 0;
    if (!tokenRegex.test(text)) return;

    tokenRegex.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const rendered = renderToken(match[0]);
      fragment.appendChild(rendered ?? document.createTextNode(match[0]));
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  });
}

const RichText: React.FC<Props> = ({ html, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // Transform custom attributes → real HTML before sanitizing
  const transformedHtml = useMemo(() => {
    return html
      .replace(/\bcontainerstyle\s*=\s*"([^"]*)"/gi, (_m, css) => `style="${css}"`)
      .replace(/\bwrapperstyle\s*=\s*"[^"]*"/gi, '');
  }, [html]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.innerHTML = DOMPurify.sanitize(transformedHtml, {
      ADD_ATTR: ['style'],
    });

    normaliseTiptapMathNodes(el);
    renderDelimitedMathTextNodes(el);

    renderMathInElement(el, {
      delimiters: [
        { left: '$$', right: '$$', display: true },
        { left: '\\[', right: '\\]', display: true },
        { left: '$', right: '$', display: false },
        { left: '\\(', right: '\\)', display: false },
      ],
      throwOnError: false,
    });
  }, [transformedHtml, html]);

  const wrapperClass = ['rt-root', className].filter(Boolean).join(' ');

  const scopedCss = `

    .rt-root table {
      border-collapse: collapse;
      width: 100%;
    }

    .rt-root table,
    .rt-root th,
    .rt-root td {
      border: 1px solid ${theme.palette.divider};
    }

    .rt-root th,
    .rt-root td {
      padding: 6px 8px;
      vertical-align: top;
    }

    .rt-root thead th {
      font-weight: 600;
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: scopedCss }} />
      <div ref={containerRef} className={wrapperClass} />
    </>
  );
};

export default RichText;
